import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFieldAuth } from '../../contexts/FieldAuthContext';
import { Visit, Workplace } from '../../types';
import { visitApi, workplaceApi } from '../../services/api';
import { canMarkVisitAsCompleted } from '../../utils/dateUtils';
import Swal from 'sweetalert2';
import './FieldPlanningPage.css';

const VISIT_TYPES = [
  'Periyodik Saha Ziyareti (Rutin)',
  'Acil / Olay Sonrası Ziyaret',
  'Risk Analizi Saha Turu',
  'Eğitim & Tatbikat Ziyareti',
  'Kurul / Danışmanlık Toplantısı'
];

const getHazardColor = (dc?: string) => {
  const s = (dc || '').toLowerCase().trim();
  if (s.includes('çok') || s.includes('cok') || s.includes('high') || s.includes('very')) return '#F44336';
  if (s.includes('az') || s.includes('low') || s.includes('less')) return '#4CAF50';
  return '#FF9800';
};

const getHazardText = (dc?: string) => {
  const s = (dc || '').toLowerCase().trim();
  if (s.includes('çok') || s.includes('cok') || s.includes('high') || s.includes('very')) return 'Çok Tehlikeli';
  if (s.includes('az') || s.includes('low') || s.includes('less')) return 'Az Tehlikeli';
  return 'Tehlikeli';
};

const FieldPlanningPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, logout } = useFieldAuth();
  const todayStr = useMemo(() => new Date().toISOString().split('T')[0], []);

  // Data States
  const [visits, setVisits] = useState<Visit[]>([]);
  const [workplaces, setWorkplaces] = useState<Workplace[]>([]);
  const [loading, setLoading] = useState(true);

  // Filtreler & Aktif Tarih
  const [selectedDate, setSelectedDate] = useState<string>(todayStr);
  const [filterMode, setFilterMode] = useState<'selected_day' | 'all' | 'planned' | 'completed'>('selected_day');
  const [searchTerm, setSearchTerm] = useState('');

  // Yeni Plan Modalı State'leri
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalWpSearch, setModalWpSearch] = useState('');
  const [modalSelectedWp, setModalSelectedWp] = useState<Workplace | null>(null);
  const [modalVisitDate, setModalVisitDate] = useState(todayStr);
  const [modalStartTime, setModalStartTime] = useState('09:00');
  const [modalEndTime, setModalEndTime] = useState('11:00');
  const [modalVisitType, setModalVisitType] = useState(VISIT_TYPES[0]);
  const [modalNotes, setModalNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [modalMarkCompleted, setModalMarkCompleted] = useState(false);

  // Geçmiş Tarih Kontrolü ve Gün Farkı Hesabı
  const isModalPastDate = Boolean(modalVisitDate && modalVisitDate < todayStr);
  const modalPastDaysCount = useMemo(() => {
    if (!modalVisitDate || modalVisitDate >= todayStr) return 0;
    const target = new Date(modalVisitDate + 'T00:00:00');
    const now = new Date(todayStr + 'T00:00:00');
    return Math.max(1, Math.round((now.getTime() - target.getTime()) / (1000 * 60 * 60 * 24)));
  }, [modalVisitDate, todayStr]);
  const modalPastDaysLabel = modalPastDaysCount === 1 ? '1 gün önce / Dün' : `${modalPastDaysCount} gün önce`;

  // Verileri Backend'den Yükle
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [visitsRes, wpRes] = await Promise.all([
        visitApi.getAll(),
        workplaceApi.getAll()
      ]);

      if (visitsRes && visitsRes.success && visitsRes.data) {
        setVisits(visitsRes.data);
      }
      if (wpRes && wpRes.success && wpRes.data) {
        setWorkplaces(wpRes.data);
      }
    } catch (err) {
      console.error('Planlama verileri yüklenirken hata:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // 14 Günlük Tarih Şeridi (Bugünün 3 gün öncesinden başlar, 10 gün sonrasına kadar)
  const dateStripList = useMemo(() => {
    const list = [];
    const base = new Date();
    base.setDate(base.getDate() - 3);

    const DAY_NAMES = ['Paz', 'Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt'];

    for (let i = 0; i < 14; i++) {
      const d = new Date(base);
      d.setDate(base.getDate() + i);
      const dateStr = d.toISOString().split('T')[0];
      const dayNum = d.getDate();
      const dayName = DAY_NAMES[d.getDay()];
      const isToday = dateStr === todayStr;
      const count = visits.filter(v => v.visitDate === dateStr && v.status !== 'cancelled').length;

      list.push({
        dateStr,
        dayNum,
        dayName,
        isToday,
        count
      });
    }
    return list;
  }, [todayStr, visits]);

  // KPI İstatistikleri
  const stats = useMemo(() => {
    const now = new Date();
    const monthPrefix = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;

    const thisMonthVisits = visits.filter(v => (v.visitDate || '').startsWith(monthPrefix));
    const plannedCount = thisMonthVisits.filter(v => v.status === 'planned').length;
    const completedCount = thisMonthVisits.filter(v => v.status === 'completed').length;
    const todayCount = visits.filter(v => v.visitDate === todayStr && v.status === 'planned').length;

    return {
      todayPlanned: todayCount,
      monthPlanned: plannedCount,
      monthCompleted: completedCount
    };
  }, [visits, todayStr]);

  // Filtrelenmiş Ziyaretler Listesi
  const filteredVisits = useMemo(() => {
    return visits.filter((v) => {
      // 1. Mod Filtresi
      if (filterMode === 'selected_day') {
        if (v.visitDate !== selectedDate) return false;
      } else if (filterMode === 'planned') {
        if (v.status !== 'planned') return false;
      } else if (filterMode === 'completed') {
        if (v.status !== 'completed') return false;
      }

      // 2. Arama Filtresi
      if (searchTerm.trim()) {
        const q = searchTerm.toLocaleLowerCase('tr-TR');
        const wpTitle = (v.workplace?.title || '').toLocaleLowerCase('tr-TR');
        const notes = (v.notes || '').toLocaleLowerCase('tr-TR');
        const type = (v.visitType || '').toLocaleLowerCase('tr-TR');
        if (!wpTitle.includes(q) && !notes.includes(q) && !type.includes(q)) {
          return false;
        }
      }

      return true;
    }).sort((a, b) => {
      // Sıralama: Tarihe ve ardından saate göre
      if (a.visitDate !== b.visitDate) {
        return a.visitDate.localeCompare(b.visitDate);
      }
      return (a.startTime || '').localeCompare(b.startTime || '');
    });
  }, [visits, selectedDate, filterMode, searchTerm]);

  // Ziyareti Tamamla
  const handleCompleteVisit = async (visit: Visit) => {
    const timeCheck = canMarkVisitAsCompleted(visit.visitDate, visit.startTime);
    if (!timeCheck.allowed) {
      Swal.fire({
        icon: 'warning',
        title: 'Ziyaret Henüz Başlayamaz',
        text: timeCheck.reason || 'Ziyaret günü ve saati gelmeden "YAPILDI" olarak işaretlenemez.',
        confirmButtonColor: '#e11d48'
      });
      return;
    }

    const { value: notes } = await Swal.fire({
      title: 'Ziyareti Tamamla',
      text: `"${visit.workplace?.title || 'Firma'}" saha ziyaretini tamamlandı olarak kaydetmek istiyor musunuz?`,
      input: 'textarea',
      inputLabel: 'Saha Denetim Notu / Öneriler',
      inputValue: visit.notes || '',
      inputPlaceholder: 'Saha tespitleri, çalışan uyarıları...',
      showCancelButton: true,
      confirmButtonColor: '#16a34a',
      cancelButtonColor: '#64748b',
      confirmButtonText: '✓ Tamamlandı Olarak Kaydet',
      cancelButtonText: 'Vazgeç'
    });

    if (notes !== undefined) {
      try {
        const res = await visitApi.updateStatus(visit.id, {
          status: 'completed',
          notes: notes ? notes.trim() : visit.notes
        });
        if (res && res.success) {
          Swal.fire({
            icon: 'success',
            title: 'Tebrikler!',
            text: 'Ziyaret başarıyla tamamlandı.',
            timer: 1500,
            showConfirmButton: false
          });
          loadData();
        }
      } catch (err: any) {
        Swal.fire({
          icon: 'error',
          title: 'Hata',
          text: err.response?.data?.message || 'Ziyaret güncellenemedi.'
        });
      }
    }
  };

  // WhatsApp ile "Yoldayım / Geliyorum" Bildirimi
  const handleSendWhatsAppArrival = (visit: Visit) => {
    const wp = visit.workplace;
    const phone = wp?.contactPhone;
    if (!phone) {
      Swal.fire({
        icon: 'info',
        title: 'Telefon Bulunamadı',
        text: 'Bu işyeri için kayıtlı bir yetkili telefon numarası bulunmuyor. Düzenleme ekranından ekleyebilirsiniz.',
        confirmButtonColor: '#2563eb'
      });
      return;
    }

    const cleanPhone = phone.replace(/\D/g, '');
    const formattedPhone = cleanPhone.startsWith('90')
      ? cleanPhone
      : cleanPhone.startsWith('0')
      ? '9' + cleanPhone
      : '90' + cleanPhone;

    const contactPerson = wp?.contactPerson ? `${wp.contactPerson} Bey/Hanım` : 'Yetkili';
    const timeStr = visit.startTime ? `saat ${visit.startTime} civarında` : 'kısa süre içinde';

    const message = `Merhaba ${contactPerson}, İSG Uzmanınız olarak bugünkü rutin saha denetimimiz için ${timeStr} firmanızda olacağım. Bilginize sunar, iyi çalışmalar dilerim.`;

    const waUrl = `https://wa.me/${formattedPhone}?text=${encodeURIComponent(message)}`;
    window.open(waUrl, '_blank');
  };

  // Günün Çoklu Ziyaret Rotasını Google Maps'te Aç
  const handleOpenFullRoute = () => {
    // Sadece adresi olan ve iptal edilmemiş ziyaretleri al
    const validVisits = filteredVisits.filter(v => v.workplace?.address && v.status !== 'cancelled');
    if (validVisits.length === 0) {
      Swal.fire({
        icon: 'warning',
        title: 'Adres Bulunamadı',
        text: 'Rotası çizilecek işyerlerinin kayıtlı adres bilgisi bulunmuyor.',
        confirmButtonColor: '#2563eb'
      });
      return;
    }

    if (validVisits.length === 1) {
      // Tek durak ise doğrudan hedefe git
      const targetAddress = encodeURIComponent(validVisits[0].workplace!.address!);
      window.open(`https://www.google.com/maps/dir/?api=1&destination=${targetAddress}&travelmode=driving`, '_blank');
      return;
    }

    // Çoklu durak rotası (Origin: Mevcut Konum, Destination: Son Ziyaret, Waypoints: Ara Ziyaretler)
    const destination = encodeURIComponent(validVisits[validVisits.length - 1].workplace!.address!);
    const waypoints = validVisits
      .slice(0, validVisits.length - 1)
      .map(v => encodeURIComponent(v.workplace!.address!))
      .join('|');

    const multiStopUrl = `https://www.google.com/maps/dir/?api=1&destination=${destination}&waypoints=${waypoints}&travelmode=driving`;
    window.open(multiStopUrl, '_blank');
  };

  // Ziyareti Sil
  const handleDeleteVisit = async (visit: Visit) => {
    const confirmed = await Swal.fire({
      title: 'Ziyareti Sil?',
      text: `"${visit.workplace?.title || 'Bu'}" için planlanan ziyaret takvimden kaldırılacak.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#64748b',
      confirmButtonText: 'Evet, Sil',
      cancelButtonText: 'Vazgeç'
    });

    if (confirmed.isConfirmed) {
      try {
        const res = await visitApi.delete(visit.id);
        if (res && res.success) {
          Swal.fire({
            icon: 'success',
            title: 'Silindi',
            text: 'Ziyaret planı kaldırıldı.',
            timer: 1300,
            showConfirmButton: false
          });
          loadData();
        }
      } catch (err: any) {
        Swal.fire({
          icon: 'error',
          title: 'Hata',
          text: err.response?.data?.message || 'Ziyaret silinemedi.'
        });
      }
    }
  };

  // Yeni Plan Oluştur
  const handleCreatePlan = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!modalSelectedWp) {
      Swal.fire('Eksik Bilgi', 'Lütfen ziyaret edilecek işyerini seçin.', 'warning');
      return;
    }

    const isPast = Boolean(modalVisitDate && modalVisitDate < todayStr);

    // Geçmiş Tarih Onayı: Mobil kullanıcıya geçmiş tarihli işlem teyidi sor
    if (isPast) {
      const dayText = modalPastDaysCount === 1 ? 'dün' : `${modalPastDaysCount} gün önce`;
      const statusText = modalMarkCompleted ? 'Gerçekleşti ("Tamamlandı")' : 'Planlandı ("Bekleyen")';

      const confirmPast = await Swal.fire({
        icon: 'warning',
        title: '⚠️ Geçmiş Tarihli Ziyaret Kaydı',
        html: `
          <div style="text-align: left; font-size: 13px; line-height: 1.6; color: #1e293b;">
            <p style="margin: 0 0 8px 0;">
              <strong>${modalSelectedWp.title}</strong> işyeri için <strong>${modalVisitDate}</strong> (${dayText}) tarihine kayıt ekliyorsunuz.
            </p>
            <div style="background: #f8fafc; border: 1px solid #e2e8f0; padding: 8px 12px; border-radius: 6px; margin-bottom: 8px;">
              📌 Kayıt Durumu: <strong>${statusText}</strong>
            </div>
            <p style="margin: 0; font-size: 12px; color: #64748b;">
              Bu geçmiş ziyaret kaydını onaylıyor musunuz?
            </p>
          </div>
        `,
        showCancelButton: true,
        confirmButtonText: '✓ Evet, Kaydet',
        cancelButtonText: 'Tarihi Değiştir',
        confirmButtonColor: '#059669',
        cancelButtonColor: '#64748b'
      });

      if (!confirmPast.isConfirmed) {
        return;
      }
    }

    try {
      setIsSubmitting(true);
      const res = await visitApi.create({
        workplaceId: modalSelectedWp.id,
        visitDate: modalVisitDate,
        startTime: modalStartTime,
        endTime: modalEndTime,
        visitType: modalVisitType,
        notes: modalNotes.trim(),
        status: isPast && modalMarkCompleted ? 'completed' : 'planned'
      });

      if (res && res.success) {
        const titleText = isPast && modalMarkCompleted ? 'Ziyaret Kaydedildi!' : 'Plan Eklendi!';
        const bodyText = isPast && modalMarkCompleted
          ? `${modalVisitDate} tarihli saha ziyareti "Tamamlandı" olarak sisteme işlendi.`
          : 'Yeni saha ziyareti takvime işlendi.';

        Swal.fire({
          icon: 'success',
          title: titleText,
          text: bodyText,
          timer: 1600,
          showConfirmButton: false
        });
        setIsModalOpen(false);
        setModalSelectedWp(null);
        setModalNotes('');
        setModalMarkCompleted(false);
        setSelectedDate(modalVisitDate);
        setFilterMode('selected_day');
        loadData();
      }
    } catch (err: any) {
      Swal.fire({
        icon: 'error',
        title: 'Hata',
        text: err.response?.data?.message || 'Ziyaret planı oluşturulurken hata oluştu.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Modal Arama Filtresi
  const modalWpSuggestions = useMemo(() => {
    const q = modalWpSearch.toLocaleLowerCase('tr-TR').trim();
    if (!q) return workplaces.slice(0, 8);
    return workplaces.filter(w =>
      (w.title || '').toLocaleLowerCase('tr-TR').includes(q) ||
      (w.sgkNo || '').includes(q) ||
      (w.city || '').toLocaleLowerCase('tr-TR').includes(q)
    ).slice(0, 10);
  }, [workplaces, modalWpSearch]);

  return (
    <div className="fplan-container">
      {/* 1. Üst Başlık & KPI Şeridi */}
      <header className="fplan-header">
        <div className="fplan-header-top">
          <div className="fplan-title-group">
            <span className="fplan-title-icon">📅</span>
            <h1 className="fplan-title">Ziyaret & Planlama</h1>
          </div>

          <div className="header-nav-group">
            <button
              type="button"
              className="header-nav-btn"
              onClick={() => navigate('/field/dashboard')}
            >
              <span className="nav-icon">🏠</span>
              <span className="nav-text">Ana Sayfa</span>
            </button>

            <button
              type="button"
              className="header-nav-btn active"
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            >
              <span className="nav-icon">📅</span>
              <span className="nav-text">Planlama</span>
            </button>

            <button
              type="button"
              className="header-nav-btn"
              onClick={() => {
                if (window.confirm('Mobil saha oturumunuzu kapatmak istiyor musunuz?')) {
                  logout();
                  navigate('/field/login', { replace: true });
                }
              }}
            >
              <span className="nav-icon">🚪</span>
              <span className="nav-text">Çıkış</span>
            </button>
          </div>
        </div>

        {/* 3'lü KPI */}
        <div className="fplan-kpi-row">
          <div className="fplan-kpi-card">
            <span className="fplan-kpi-val" style={{ color: '#38bdf8' }}>{stats.todayPlanned}</span>
            <span className="fplan-kpi-lbl">Bugün Planlanan</span>
          </div>
          <div className="fplan-kpi-card">
            <span className="fplan-kpi-val" style={{ color: '#facc15' }}>{stats.monthPlanned}</span>
            <span className="fplan-kpi-lbl">Bu Ay Bekleyen</span>
          </div>
          <div className="fplan-kpi-card">
            <span className="fplan-kpi-val" style={{ color: '#4ade80' }}>{stats.monthCompleted}</span>
            <span className="fplan-kpi-lbl">Bu Ay Yapılan</span>
          </div>
        </div>
      </header>

      {/* 2. Yatay Tarih Şeridi (Date Strip Carousel) */}
      <div className="fplan-date-strip-wrapper">
        {dateStripList.map(item => {
          const isSelected = selectedDate === item.dateStr && filterMode === 'selected_day';
          return (
            <div
              key={item.dateStr}
              className={`fplan-date-pill ${item.isToday ? 'today' : ''} ${isSelected ? 'selected' : ''}`}
              onClick={() => {
                setSelectedDate(item.dateStr);
                setFilterMode('selected_day');
              }}
            >
              <span className="fplan-pill-day">{item.isToday ? 'Bugün' : item.dayName}</span>
              <span className="fplan-pill-num">{item.dayNum}</span>
              {item.count > 0 && <span className="fplan-pill-dot" />}
            </div>
          );
        })}
      </div>

      {/* 3. Arama & Filtre Tabları */}
      <div className="fplan-controls-bar">
        <div className="fplan-search-input-box">
          <span className="fplan-search-icon">🔍</span>
          <input
            type="text"
            placeholder="İşyeri veya not ara..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="fplan-filter-tabs">
          <button
            type="button"
            className={`fplan-filter-tab ${filterMode === 'selected_day' ? 'active' : ''}`}
            onClick={() => setFilterMode('selected_day')}
          >
            Seçili Gün ({selectedDate.split('-').slice(1).reverse().join('/')})
          </button>
          <button
            type="button"
            className={`fplan-filter-tab ${filterMode === 'planned' ? 'active' : ''}`}
            onClick={() => setFilterMode('planned')}
          >
            Bekleyenler
          </button>
          <button
            type="button"
            className={`fplan-filter-tab ${filterMode === 'completed' ? 'active' : ''}`}
            onClick={() => setFilterMode('completed')}
          >
            Yapılanlar
          </button>
          <button
            type="button"
            className={`fplan-filter-tab ${filterMode === 'all' ? 'active' : ''}`}
            onClick={() => setFilterMode('all')}
          >
            Tüm Ziyaretler
          </button>
        </div>
      </div>

      {/* 4. Ziyaret Kartları Listesi */}
      <section className="fplan-list-section">
        <div className="fplan-list-header">
          <h2 className="fplan-list-title">
            {filterMode === 'selected_day' ? `Seçili Gün Ziyaretleri (${filteredVisits.length})` : `Ziyaretler (${filteredVisits.length})`}
          </h2>
          {filteredVisits.some(v => v.workplace?.address && v.status !== 'cancelled') && (
            <button
              type="button"
              className="fplan-route-btn-header"
              onClick={handleOpenFullRoute}
              title="Günün tüm duraklarını navigasyonda birleştir"
            >
              🗺️ Rotayı Başlat ({filteredVisits.filter(v => v.workplace?.address && v.status !== 'cancelled').length} Durak)
            </button>
          )}
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '30px', color: '#64748b' }}>
            <p>Ziyaret planları yükleniyor...</p>
          </div>
        ) : filteredVisits.length === 0 ? (
          <div className="fplan-empty-state">
            <span className="fplan-empty-icon">📅</span>
            <p>
              {filterMode === 'selected_day'
                ? 'Bu tarihte planlanmış bir saha ziyareti bulunmuyor.'
                : 'Kriterlere uygun ziyaret planı bulunamadı.'}
            </p>
            <button
              type="button"
              className="fplan-empty-add-btn"
              onClick={() => {
                setModalVisitDate(selectedDate);
                setIsModalOpen(true);
              }}
            >
              ➕ Bu Güne Ziyaret Planla
            </button>
          </div>
        ) : (
          filteredVisits.map(visit => {
            const isCompleted = visit.status === 'completed';
            const wp = visit.workplace;
            const hazardColor = getHazardColor(wp?.dangerClass);
            const hazardText = getHazardText(wp?.dangerClass);

            return (
              <div
                key={visit.id}
                className={`fplan-card ${visit.status}`}
              >
                {/* Kart Üst Başlık & Durum */}
                <div className="fplan-card-top">
                  <h3
                    className="fplan-card-title"
                    onClick={() => wp && navigate(`/field/workplace/${wp.id}`)}
                    style={{ cursor: 'pointer' }}
                  >
                    {wp?.title || 'Bilinmeyen İşyeri'}
                  </h3>
                  <span className={`fplan-status-badge ${visit.status}`}>
                    {isCompleted ? '✅ Yapıldı' : '🏃 Planlandı'}
                  </span>
                </div>

                {/* Saat & Tür Çipleri */}
                <div className="fplan-card-time-row">
                  <span className="fplan-time-chip">
                    ⏰ {visit.startTime || '09:00'} - {visit.endTime || '11:00'}
                  </span>
                  <span className="fplan-type-chip">
                    📋 {visit.visitType || 'Rutin Ziyaret'}
                  </span>
                  {wp?.dangerClass && (
                    <span className="fplan-hazard-chip" style={{ color: hazardColor }}>
                      ⚠️ {hazardText}
                    </span>
                  )}
                </div>

                {/* Konum & İletişim */}
                <div className="fplan-card-meta">
                  {wp?.address && (
                    <div className="fplan-meta-item">
                      <span>📍</span>
                      <a
                        href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(wp.address)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="fplan-meta-link"
                        title="Bu firmaya yol tarifi al"
                      >
                        {wp.address}
                      </a>
                    </div>
                  )}
                  {wp?.contactPhone && (
                    <div className="fplan-meta-item">
                      <span>📞</span>
                      <a href={`tel:${wp.contactPhone}`} className="fplan-meta-link">
                        {wp.contactPhone} {wp.contactPerson ? `(${wp.contactPerson})` : ''}
                      </a>
                    </div>
                  )}
                </div>

                {/* Saha Notu (Varsa) */}
                {visit.notes && (
                  <div className="fplan-notes-box">
                    <strong>Not:</strong> {visit.notes}
                  </div>
                )}

                {/* Aksiyon Butonları */}
                <div className="fplan-card-actions">
                  {!isCompleted && wp?.contactPhone && (
                    <button
                      type="button"
                      className="fplan-btn-action fplan-btn-wa"
                      onClick={() => handleSendWhatsAppArrival(visit)}
                      title="Firmaya yola çıktığınızı WhatsApp ile bildirin"
                    >
                      💬 Geliyorum
                    </button>
                  )}
                  {!isCompleted && (
                    <button
                      type="button"
                      className="fplan-btn-action fplan-btn-complete"
                      onClick={() => handleCompleteVisit(visit)}
                    >
                      ✓ Tamamla
                    </button>
                  )}
                  {wp && (
                    <button
                      type="button"
                      className="fplan-btn-action fplan-btn-detail"
                      onClick={() => navigate(`/field/workplace/${wp.id}`)}
                    >
                      🏢 Dosya
                    </button>
                  )}
                  <button
                    type="button"
                    className="fplan-btn-action fplan-btn-delete"
                    onClick={() => handleDeleteVisit(visit)}
                    title="Planı Sil"
                  >
                    🗑️
                  </button>
                </div>
              </div>
            );
          })
        )}
      </section>

      {/* 5. Yüzen Ekleme Butonu (FAB) */}
      <button
        type="button"
        className="fplan-fab"
        onClick={() => {
          setModalVisitDate(selectedDate);
          setIsModalOpen(true);
        }}
      >
        <span className="fplan-fab-icon">➕</span>
        <span>Plan Ekle</span>
      </button>

      {/* 6. Alttan Açılan Yeni Plan Modalı */}
      {isModalOpen && (
        <div className="fplan-modal-backdrop" onClick={() => setIsModalOpen(false)}>
          <div className="fplan-modal-sheet" onClick={(e) => e.stopPropagation()}>
            <div className="fplan-modal-header">
              <h3>➕ Yeni Saha Ziyareti Planla</h3>
              <button
                type="button"
                className="fplan-modal-close-btn"
                onClick={() => setIsModalOpen(false)}
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleCreatePlan} className="fplan-modal-body">
              {/* İşyeri Seçici */}
              <div className="fplan-form-group">
                <label className="fplan-form-label">İşyeri Seçin *</label>
                {modalSelectedWp ? (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '10px 12px',
                      background: '#eff6ff',
                      border: '1.5px solid #bfdbfe',
                      borderRadius: '8px'
                    }}
                  >
                    <div>
                      <strong style={{ fontSize: '0.85rem', color: '#1e3c72' }}>
                        {modalSelectedWp.title}
                      </strong>
                      <div style={{ fontSize: '0.72rem', color: '#64748b' }}>
                        SGK: {modalSelectedWp.sgkNo || '-'}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setModalSelectedWp(null)}
                      style={{
                        background: 'transparent',
                        border: 'none',
                        color: '#dc2626',
                        fontWeight: 750,
                        cursor: 'pointer'
                      }}
                    >
                      Değiştir
                    </button>
                  </div>
                ) : (
                  <>
                    <input
                      type="text"
                      className="fplan-form-input"
                      placeholder="İşyeri veya SGK ara..."
                      value={modalWpSearch}
                      onChange={(e) => setModalWpSearch(e.target.value)}
                      autoFocus
                    />
                    <div className="fplan-suggestions-list">
                      {modalWpSuggestions.map(w => (
                        <div
                          key={w.id}
                          className="fplan-suggestion-item"
                          onClick={() => setModalSelectedWp(w)}
                        >
                          <span className="fplan-suggestion-name">{w.title}</span>
                          <span className="fplan-suggestion-meta">
                            SGK: {w.sgkNo || '-'} • {w.city || ''}
                          </span>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>

              {/* Tarih & Saatler */}
              <div className="fplan-form-group">
                <label className="fplan-form-label">Ziyaret Tarihi *</label>
                <input
                  type="date"
                  className={`fplan-form-input ${isModalPastDate ? 'fplan-input-past' : ''}`}
                  value={modalVisitDate}
                  onChange={(e) => {
                    const d = e.target.value;
                    setModalVisitDate(d);
                    if (d && d < todayStr) {
                      setModalMarkCompleted(true);
                    } else {
                      setModalMarkCompleted(false);
                    }
                  }}
                  required
                />

                {/* Geçmiş Tarih Akıllı Uyarı & Doğrudan Tamamlama Bandı */}
                {isModalPastDate && (
                  <div className="fplan-past-date-banner">
                    <div className="fplan-past-banner-header">
                      <span className="fplan-past-icon">⚠️</span>
                      <div className="fplan-past-text">
                        <strong>Geçmiş Tarihli Planlama:</strong> Bugünün öncesinde bir tarih seçtiniz ({modalPastDaysLabel})
                      </div>
                    </div>
                    <label className="fplan-past-checkbox-label">
                      <input
                        type="checkbox"
                        checked={modalMarkCompleted}
                        onChange={(e) => setModalMarkCompleted(e.target.checked)}
                      />
                      <span>
                        Bu ziyaret gerçekleşti (Doğrudan <strong>"Tamamlandı"</strong> kaydet)
                      </span>
                    </label>
                  </div>
                )}
              </div>

              <div className="fplan-form-row">
                <div className="fplan-form-group">
                  <label className="fplan-form-label">Başlangıç Saati</label>
                  <input
                    type="time"
                    className="fplan-form-input"
                    value={modalStartTime}
                    onChange={(e) => setModalStartTime(e.target.value)}
                  />
                </div>
                <div className="fplan-form-group">
                  <label className="fplan-form-label">Bitiş Saati</label>
                  <input
                    type="time"
                    className="fplan-form-input"
                    value={modalEndTime}
                    onChange={(e) => setModalEndTime(e.target.value)}
                  />
                </div>
              </div>

              {/* Ziyaret Türü */}
              <div className="fplan-form-group">
                <label className="fplan-form-label">Ziyaret Türü</label>
                <select
                  className="fplan-form-select"
                  value={modalVisitType}
                  onChange={(e) => setModalVisitType(e.target.value)}
                >
                  {VISIT_TYPES.map(t => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>

              {/* Notlar */}
              <div className="fplan-form-group">
                <label className="fplan-form-label">Saha Notu / Hatırlatıcı</label>
                <textarea
                  className="fplan-form-textarea"
                  rows={2}
                  placeholder="Görüşülecek kişi, denetlenecek departman..."
                  value={modalNotes}
                  onChange={(e) => setModalNotes(e.target.value)}
                />
              </div>

              <button
                type="submit"
                className={`fplan-modal-submit-btn ${isModalPastDate && modalMarkCompleted ? 'fplan-modal-submit-btn-completed' : ''}`}
                disabled={isSubmitting || !modalSelectedWp}
              >
                {isSubmitting
                  ? 'Kaydediliyor...'
                  : isModalPastDate && modalMarkCompleted
                  ? '✓ Ziyareti Gerçekleşti Olarak Kaydet'
                  : '✓ Ziyaret Planını Kaydet'}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default FieldPlanningPage;
